/**
 * Rekenwerk van de lijst, los van het scherm: tijden in Amsterdamse tijd,
 * de wachttijd in woorden, de volgorde van de kaarten en de dagen van de
 * map Afgerond.
 */
import {
  KANALEN,
  MAPPEN,
  type Groep,
  type Kanaal,
  type Map as MapNaam,
  type Tegel,
  type V6Kaart,
} from "../api";
import type { IcoonNaam } from "../Icoon";
import type { NuBezigRij } from "../live";

import type { LaatsteBericht } from "./Kaart";

const TZ = "Europe/Amsterdam";

// ── Tegel en map in de url (sessie 70) ───────────────────────────────

/** Kanaaltegel en map staan in de url (`?kanaal=`, `?map=`), zodat ze na
 *  verversen blijven. Map Open en geen tegel staan er niet in. */
export interface UrlFilter {
  map: MapNaam;
  kanaal: Kanaal | null;
}

/** Lees tegel en map uit de url; een onbekende waarde telt als leeg. */
export function filterUitUrl(params: URLSearchParams): UrlFilter {
  const map = params.get("map");
  const kanaal = params.get("kanaal");
  return {
    map: MAPPEN.find((m) => m.map === map)?.map ?? "open",
    kanaal: KANALEN.find((k) => k === kanaal) ?? null,
  };
}

/** Zet tegel en/of map in een kopie van de url; de rest (`?zaak=`,
 *  `?chat=`, `?mail=`) blijft staan. */
export function filterNaarUrl(
  params: URLSearchParams,
  wijziging: Partial<UrlFilter>,
): URLSearchParams {
  const nieuw = new URLSearchParams(params);
  if (wijziging.map !== undefined) {
    if (wijziging.map === "open") nieuw.delete("map");
    else nieuw.set("map", wijziging.map);
  }
  if (wijziging.kanaal !== undefined) {
    if (wijziging.kanaal === null) nieuw.delete("kanaal");
    else nieuw.set("kanaal", wijziging.kanaal);
  }
  return nieuw;
}

/** Css-klasse per kanaal, zoals in de mock (wa, tel). */
export const KANAAL_KLASSE: Record<Kanaal, string> = {
  mail: "mail",
  chat: "chat",
  whatsapp: "wa",
  telefoon: "tel",
};

export const KANAAL_ICOON: Record<Kanaal, IcoonNaam> = {
  mail: "mail",
  chat: "chat",
  whatsapp: "whatsapp",
  telefoon: "telefoon",
};

const dagDelen = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const uurFmt = new Intl.DateTimeFormat("nl-NL", {
  timeZone: TZ,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
const kortFmt = new Intl.DateTimeFormat("nl-NL", {
  timeZone: TZ,
  day: "2-digit",
  month: "2-digit",
});
const langFmt = new Intl.DateTimeFormat("nl-NL", {
  timeZone: TZ,
  weekday: "long",
  day: "numeric",
  month: "long",
});

function alsDatum(d: Date | string): Date | null {
  const datum = typeof d === "string" ? new Date(d) : d;
  return Number.isNaN(datum.getTime()) ? null : datum;
}

/** De kalenderdag in Amsterdam als "2026-09-27". */
export function amsDag(d: Date | string): string {
  const datum = alsDatum(d);
  if (!datum) return "";
  const delen = dagDelen.formatToParts(datum);
  const deel = (soort: string) => delen.find((p) => p.type === soort)?.value ?? "";
  return `${deel("year")}-${deel("month")}-${deel("day")}`;
}

/** De dag vóór een kalenderdag ("2026-09-27" wordt "2026-09-26"). */
export function dagErvoor(dag: string): string {
  const [j, m, d] = dag.split("-").map(Number);
  return new Date(Date.UTC(j, m - 1, d - 1)).toISOString().slice(0, 10);
}

/** Tijd op de kaart: vandaag "09:12", gisteren "gisteren", ouder "24-09". */
export function tijdWoord(moment: string | null | undefined, nu: Date): string {
  if (!moment) return "";
  const datum = alsDatum(moment);
  if (!datum) return "";
  const dag = amsDag(datum);
  const vandaag = amsDag(nu);
  if (dag === vandaag) return uurFmt.format(datum);
  if (dag === dagErvoor(vandaag)) return "gisteren";
  return kortFmt.format(datum);
}

/** "wacht 25 min", "wacht 2 u 40", "wacht 18 u", "wacht 3 dagen". */
export function wachtWoord(sinds: string | null | undefined, nu: Date): string | null {
  if (!sinds) return null;
  const datum = alsDatum(sinds);
  if (!datum) return null;
  const minuten = Math.floor((nu.getTime() - datum.getTime()) / 60_000);
  if (minuten < 1) return null;
  if (minuten < 60) return `wacht ${minuten} min`;
  const uren = Math.floor(minuten / 60);
  if (uren >= 48) return `wacht ${Math.floor(uren / 24)} dagen`;
  if (uren >= 10) return `wacht ${uren} u`;
  const rest = minuten % 60;
  return rest ? `wacht ${uren} u ${rest}` : `wacht ${uren} u`;
}

export interface OpenKaart {
  kaart: V6Kaart;
  /** De groep zoals het scherm hem toont: live wint als de stroom dat zegt. */
  groep: Groep;
  /** Live volgens de live-stroom, terwijl de server een andere groep gaf. */
  liveOverschreven: boolean;
}

function tijdVan(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Eén platte lijst op tijd van het laatste bericht, nieuwste boven
 * (sessie 75). Alleen een lopend gesprek (live) staat daarboven, ook dan
 * nieuwste eerst. Een kaart die volgens de live-stroom live is, telt als
 * live, ook als de server een andere groep gaf.
 */
export function sorteerOpen(
  kaarten: V6Kaart[],
  isLive: (ids: string[]) => boolean,
): OpenKaart[] {
  return kaarten
    .map((kaart) => {
      const stroomLive = kaart.groep !== "live" && isLive(kaart.gesprek_ids);
      return {
        kaart,
        groep: stroomLive ? ("live" as Groep) : kaart.groep,
        liveOverschreven: stroomLive,
      };
    })
    .sort((a, b) => {
      const rang = Number(b.groep === "live") - Number(a.groep === "live");
      if (rang !== 0) return rang;
      return tijdVan(b.kaart.moment) - tijdVan(a.kaart.moment);
    });
}

/**
 * Rij "Live nu" bovenaan Voor jou (28-10): de gesprekken waar Nova op dit
 * moment live mee bezig is en die onder een andere tegel staan (Wacht).
 * Een venster, geen verplaatsing: de kaart blijft onder zijn eigen tegel en
 * telt niet mee in Voor jou. Een live kaart die al in Voor jou staat, staat
 * daar al bovenaan en komt hier niet nog eens.
 */
export function liveNu(kaarten: V6Kaart[], isLive: (ids: string[]) => boolean): OpenKaart[] {
  return sorteerOpen(
    kaarten.filter((k) => tegelVan(k) !== "voor_jou"),
    isLive,
  ).filter((o) => o.groep === "live");
}

export interface Dag {
  sleutel: string;
  kop: string;
  vandaag: boolean;
  kaarten: V6Kaart[];
}

function hoofdletter(tekst: string): string {
  return tekst.charAt(0).toUpperCase() + tekst.slice(1);
}

/** Afgeronde kaarten per dag (nieuwste dag eerst) met de kop van de mock. */
export function perDag(kaarten: V6Kaart[], nu: Date): Dag[] {
  const vandaag = amsDag(nu);
  const gisteren = dagErvoor(vandaag);
  // Sessie 75: ook Afgerond, Spam en Systeem op het laatste bericht.
  const moment = (k: V6Kaart) => k.moment;
  const gesorteerd = [...kaarten].sort((a, b) => tijdVan(moment(b)) - tijdVan(moment(a)));
  const dagen: Dag[] = [];
  for (const kaart of gesorteerd) {
    const sleutel = amsDag(moment(kaart)) || "onbekend";
    let dag = dagen.find((d) => d.sleutel === sleutel);
    if (!dag) {
      dag = { sleutel, kop: "", vandaag: sleutel === vandaag, kaarten: [] };
      dagen.push(dag);
    }
    dag.kaarten.push(kaart);
  }
  for (const dag of dagen) {
    const datum = alsDatum(moment(dag.kaarten[0]));
    const lang = datum ? langFmt.format(datum) : "onbekende dag";
    if (dag.sleutel === vandaag) dag.kop = "Vandaag";
    else if (dag.sleutel === gisteren) dag.kop = `Gisteren, ${lang} (${dag.kaarten.length})`;
    else dag.kop = `${hoofdletter(lang)} (${dag.kaarten.length})`;
  }
  return dagen;
}

export interface VerwerkUitkomst {
  /** Verwerkt: deze kaarten verdwijnen uit de lijst. */
  geslaagd: string[];
  /** Mislukt, met de reden per zaak: deze blijven aangevinkt. */
  fouten: Record<string, string>;
}

/**
 * Leg de resultaten van de bulk-API naast de gevraagde zaken. Een zaak
 * zonder resultaat telt als mislukt: nooit stil een kaart laten vallen.
 */
export function leesVerwerk(
  gevraagd: string[],
  resultaten: { zaak_id: string; ok: boolean; melding: string }[],
): VerwerkUitkomst {
  const perZaak = new Map(resultaten.map((r) => [r.zaak_id, r]));
  const geslaagd: string[] = [];
  const fouten: Record<string, string> = {};
  for (const id of gevraagd) {
    const r = perZaak.get(id);
    if (r?.ok) geslaagd.push(id);
    else fouten[id] = r?.melding?.trim() || (r ? "Verwerken mislukt." : "Geen uitkomst ontvangen.");
  }
  return { geslaagd, fouten };
}

/**
 * Wat een kaart op het moment van aanvinken zegt. Verandert dit daarna
 * (nieuw bericht, andere toelichting of stand), dan klopt het vinkje niet
 * meer: JP vinkte iets anders aan dan er nu staat. De kaart draagt het
 * concept zelf niet; de toelichting ("concept klaar") en het moment wel.
 */
export function vingerafdruk(kaart: V6Kaart): string {
  return [
    kaart.moment,
    kaart.toelichting ?? "",
    kaart.onderwerp,
    kaart.status_woord,
    kaart.stand ?? "",
    kaart.vinkbaar ? "v" : "",
  ].join("|");
}

/**
 * Timeout of netwerkfout: er kwam geen antwoord van de server. Dan weten
 * we niet of de zaken verwerkt zijn; alleen een verse lijst kan dat zeggen.
 */
export function uitkomstOnbekend(fout: unknown): boolean {
  if (!fout || typeof fout !== "object") return true;
  return !(fout as { response?: unknown }).response;
}

export interface VerwerkTelling {
  verwerkt: number;
  mislukt: number;
  /** Geen antwoord (timeout, netwerk): de verse lijst beslist. */
  onbekend: number;
  /** Na geen antwoord niet meer gestuurd; blijven aangevinkt. */
  nietVerstuurd: number;
}

/** "2 verwerkt, 1 mislukt", ook als alles mislukt; de rest alleen als het er is. */
export function samenvatting(t: VerwerkTelling): string {
  const delen = [`${t.verwerkt} verwerkt`, `${t.mislukt} mislukt`];
  if (t.onbekend > 0) delen.push(`${t.onbekend} onbekend`);
  if (t.nietVerstuurd > 0) delen.push(`${t.nietVerstuurd} niet verstuurd`);
  return delen.join(", ");
}

/** Het nieuwste bericht van de gesprekken van een kaart volgens de live-stand. */
export function laatsteBericht(
  rijen: Map<string, NuBezigRij> | undefined,
  gesprekIds: string[],
): LaatsteBericht | null {
  let beste: LaatsteBericht | null = null;
  for (const id of gesprekIds) {
    const rij = rijen?.get(id);
    if (!rij?.laatste_regel) continue;
    if (!beste || new Date(rij.laatste_op).getTime() > new Date(beste.op).getTime()) {
      beste = { regel: rij.laatste_regel, op: rij.laatste_op };
    }
  }
  return beste;
}

// ── Tegels en kaarttijden (motor 7 golf 2, sessie D) ─────────────────

/** De tegel in de url (`?tegel=`): Voor jou staat er niet in; Afgerond is de
 *  map Afgerond (`?map=afgerond`). */
export function tegelUitUrl(params: URLSearchParams, map: MapNaam): Tegel | null {
  if (map === "afgerond") return "afgerond";
  if (map !== "open") return null;
  const t = params.get("tegel");
  return t === "nakijken" || t === "wacht" ? t : "voor_jou";
}

/** Zet de tegel in een kopie van de url; kanaal en belofte-filter gaan eruit. */
export function tegelNaarUrl(params: URLSearchParams, tegel: Tegel): URLSearchParams {
  const nieuw = filterNaarUrl(params, {
    map: tegel === "afgerond" ? "afgerond" : "open",
    kanaal: null,
  });
  nieuw.delete("belofte");
  if (tegel === "nakijken" || tegel === "wacht") nieuw.set("tegel", tegel);
  else nieuw.delete("tegel");
  return nieuw;
}

/** De tegel van een open kaart: wat de server zei, of Voor jou. */
export function tegelVan(kaart: V6Kaart): Tegel {
  return kaart.tegel ?? "voor_jou";
}

export type TegelTellers = Record<Tegel, number>;

/** De vier getallen uit de open kaarten zelf, zodat ze meteen meebewegen als een
 *  kaart van tegel wisselt. Afgerond telt ook wat vandaag al dicht ging: precies
 *  de kaarten onder "Vandaag" in de map Afgerond (`splitsAfgerond`, sessie 161). */
export function telTegels(kaarten: V6Kaart[], afgerondVandaag: number): TegelTellers {
  const uit: TegelTellers = { voor_jou: 0, nakijken: 0, wacht: 0, afgerond: afgerondVandaag };
  for (const k of kaarten) uit[tegelVan(k)] += 1;
  return uit;
}

/** Per kanaal en het aantal met een belofte, over de kaarten van een tegel. */
export function telKanalen(kaarten: V6Kaart[]): Record<Kanaal | "beloftes", number> {
  const uit = { mail: 0, chat: 0, whatsapp: 0, telefoon: 0, beloftes: 0 };
  for (const k of kaarten) {
    if (k.kanaal !== "taak") uit[k.kanaal] += 1;
    if (k.belofte_voor) uit.beloftes += 1;
  }
  return uit;
}

const dagKort = new Intl.DateTimeFormat("nl-NL", { timeZone: TZ, weekday: "short" });
const datumKort = new Intl.DateTimeFormat("nl-NL", { timeZone: TZ, day: "numeric", month: "short" });

/** "14:02" vandaag; "wo 12:24" binnen een week; ouder "12 okt 12:24". Leeg als
 *  het moment onbekend is. */
export function dagTijd(moment: string | null | undefined, nu: Date): string {
  if (!moment) return "";
  const datum = alsDatum(moment);
  if (!datum) return "";
  const uur = uurFmt.format(datum);
  if (amsDag(datum) === amsDag(nu)) return uur;
  const dagen = (nu.getTime() - datum.getTime()) / 86_400_000;
  const dag =
    dagen >= -1 && dagen < 6 ? dagKort.format(datum).replace(".", "") : datumKort.format(datum).replace(".", "");
  return `${dag} ${uur}`;
}

// ── Sessie 161: verversen bij een klantbericht, Afgerond gelijk aan de teller ──

/** Recensie 76, punt 2: een klantbericht op een kaart buiten Voor jou (of buiten
 *  Open) kan de tegel veranderen; de server rekent hem meteen opnieuw uit. Op een
 *  kaart die al in Voor jou staat verandert de tegel niet. */
export function verversBijKlantbericht(kaart: V6Kaart, inOpen: boolean): boolean {
  return !inOpen || tegelVan(kaart) !== "voor_jou";
}

export interface AfgerondDelen {
  /** Wat vandaag (Amsterdam) dicht ging: telt mee in de tegel Afgerond. */
  vandaag: V6Kaart[];
  /** Oudere dagen, onder het kopje "Eerder", los van de teller. */
  eerder: Dag[];
}

/** Recensie 76, punt 3: de map Afgerond in "Vandaag" (de dag van afronden, dezelfde
 *  regel als `afgerond_vandaag` van de server) en "Eerder" per dag. */
export function splitsAfgerond(kaarten: V6Kaart[], nu: Date): AfgerondDelen {
  const dag = amsDag(nu);
  const isVandaag = (k: V6Kaart) => amsDag(k.afgerond_op ?? k.moment) === dag;
  const vandaag = kaarten
    .filter(isVandaag)
    .sort((a, b) => tijdVan(b.moment) - tijdVan(a.moment));
  return { vandaag, eerder: perDag(kaarten.filter((k) => !isVandaag(k)), nu) };
}

/** Motor 7-redenen in woorden (de server geeft de vorm, nooit tekst). */
export const MOTOR7_REDEN_WOORD: Record<string, string> = {
  geld: "Geld",
  toezegging: "Toezegging",
  doorgezet: "Doorgezet",
  meelezer_ontbrak: "Meelezer ontbrak",
};
